import React from "react";
import { GlobalModal } from "../../../../global/GlobalModal";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import {
  formatMembershipAmount,
  formatMembershipDate,
  membershipStatusBadgeClass,
} from "../../utils/myMembershipsDirectoryHelpers";

function DetailTile({ label, value, mono }) {
  return (
    <div className="mm-modal-tile">
      <span className="mm-modal-tile__label">{label}</span>
      <span className={`mm-modal-tile__value${mono ? " mm-modal-tile__value--mono" : ""}`}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function PaymentStatusPill({ status, verified }) {
  const s = String(status || "").toLowerCase();
  let cls = "mm-pay-pill mm-pay-pill--muted";
  if (s === "paid") cls = "mm-pay-pill mm-pay-pill--paid";
  else if (s === "pending") cls = "mm-pay-pill mm-pay-pill--pending";
  else if (s === "no payment") cls = "mm-pay-pill mm-pay-pill--none";

  return (
    <span className={cls}>
      {status || "—"}
      {verified != null ? (
        <span className="mm-pay-pill__sub">
          {verified ? " · Verified" : " · Not verified"}
        </span>
      ) : null}
    </span>
  );
}

export default function MyMembershipsDetailModal({ row, onClose }) {
  const open = Boolean(row);
  const status = row?.status;
  const sourceLabel = row?.source === "renewal" ? "Renewal" : "Application";

  return (
    <GlobalModal
      open={open}
      onClose={onClose}
      size="lg"
      classNames={{ modal: "gff-modal mm-detail-modal", body: "gff-modal__body mm-detail-modal__shell" }}
    >
      {row ? (
        <div className="mm-detail-modal__body">
          <header className="mm-detail-modal__hero">
            <button
              type="button"
              className="mm-detail-modal__close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
            <div className="mm-detail-modal__hero-top">
              <span className="mm-detail-modal__source">{sourceLabel}</span>
              <span className={membershipStatusBadgeClass(status)}>{status || "—"}</span>
            </div>
            <h2 className="mm-detail-modal__title">
              {row.plan_name || row.membership_type || "Membership"}
            </h2>
            <p className="mm-detail-modal__code">{row.membership_id || "—"}</p>
          </header>

          <div className="mm-detail-modal__grid">
            <section className="mm-detail-modal__card" aria-label="Membership period">
              <h3 className="mm-detail-modal__card-title">
                <span className="mm-detail-modal__card-icon" aria-hidden>
                  ◷
                </span>
                Membership period
              </h3>
              <div className="mm-detail-modal__card-body mm-detail-modal__card-body--grid">
                <DetailTile label="Start date" value={formatMembershipDate(row.start_date)} />
                <DetailTile label="End date" value={formatMembershipDate(row.end_date)} />
                <DetailTile
                  label="Submitted"
                  value={formatMembershipDate(row.submitted_at)}
                />
                <DetailTile
                  label="Approved"
                  value={formatMembershipDate(row.approved_at)}
                />
                <DetailTile
                  label="Application verified"
                  value={row.verified ? "Yes" : "No"}
                />
                {row.remarks ? (
                  <div className="mm-modal-tile mm-modal-tile--wide">
                    <span className="mm-modal-tile__label">Remarks</span>
                    <span className="mm-modal-tile__value">{row.remarks}</span>
                  </div>
                ) : null}
              </div>
            </section>

            <section
              className="mm-detail-modal__card mm-detail-modal__card--payment"
              aria-label="Payment details"
            >
              <h3 className="mm-detail-modal__card-title">
                <span className="mm-detail-modal__card-icon" aria-hidden>
                  ₹
                </span>
                Payment details
              </h3>
              <div className="mm-detail-modal__pay-amount">
                <span className="mm-detail-modal__pay-label">Amount paid</span>
                <strong className="mm-detail-modal__pay-value">
                  {formatMembershipAmount(row.payment_amount ?? row.plan_price)}
                </strong>
                <PaymentStatusPill
                  status={row.payment_status}
                  verified={row.payment_verified}
                />
              </div>
              <div className="mm-detail-modal__card-body mm-detail-modal__card-body--grid">
                <DetailTile label="Payment date" value={formatMembershipDate(row.payment_date)} />
                <DetailTile label="Payment mode" value={row.payment_mode} />
                <DetailTile label="Receipt no." value={row.receipt_no} mono />
                <DetailTile label="Transaction ID" value={row.transaction_id} mono />
                <DetailTile
                  label="Plan price"
                  value={formatMembershipAmount(row.plan_price)}
                />
              </div>
            </section>
          </div>

          <footer className="mm-detail-modal__footer">
            <GlobalButton type="button" variant="primary" size="sm" onClick={onClose}>
              Close
            </GlobalButton>
          </footer>
        </div>
      ) : null}
    </GlobalModal>
  );
}
